import apiClient from './client';

export interface QuickBooksLinkStartResponse {
  authorization_url: string;
  state: string;
}

export interface QuickBooksLinkCompleteResponse {
  success: boolean;
  is_connected: boolean;
  company_name?: string;
  realm_id?: string;
  linked_at?: string;
  message?: string;
  error_code?: string;
  can_reset?: boolean;
  existing_company_name?: string;
  new_company_name?: string;
}

export interface QuickBooksLinkStatus {
  is_valid: boolean;
  company_name?: string;
  realm_id?: string;
  error_message?: string;
  last_verified_at?: string;
  token_expires_at?: string;
}

export interface QuickBooksAccount {
  id: string;
  name: string;
  account_type: string;
  account_sub_type?: string;
  classification?: string;
  current_balance?: number;
  active: boolean;
}

export interface TrustAccountLiabilitiesResponse {
  qbo_account_id: string | null;
  is_configured: boolean;
}

export interface QuickBooksFetchAccountsResponse {
  accounts: QuickBooksAccount[];
  total_count: number;
  synced_at: string;
}

export interface QuickBooksCompanyInfo {
  company_name: string;
  legal_name?: string;
  company_address?: string;
  country?: string;
  fiscal_year_start_month?: string;
}

export interface QuickBooksTokenRefreshResponse {
  success: boolean;
  expires_at?: string;
  message: string;
}

export interface SyncStats {
  imported: number;
  linked: number;
  updated: number;
}

export interface QuickBooksSyncResponse {
  imported_count: number;
  updated_count: number;
  skipped_count: number;
  errors: string[];
  synced_at: string;
  customer_stats?: SyncStats;
  vendor_stats?: SyncStats;
}

export const quickbooksApi = {
  /**
   * Start QuickBooks OAuth flow - get authorization URL
   */
  startLink: async (clientId: string): Promise<QuickBooksLinkStartResponse> => {
    const response = await apiClient.post<QuickBooksLinkStartResponse>(
      `/clients/${clientId}/quickbooks/link/start`
    );
    return response.data;
  },

  /**
   * Complete QuickBooks link after OAuth callback - verifies connection was successful
   */
  completeLink: async (clientId: string): Promise<QuickBooksLinkCompleteResponse> => {
    const response = await apiClient.post<QuickBooksLinkCompleteResponse>(
      `/clients/${clientId}/quickbooks/link/complete`
    );
    return response.data;
  },

  /**
   * Reset QuickBooks connection (only allowed if no QB data has been synced)
   */
  resetLink: async (clientId: string): Promise<void> => {
    await apiClient.post(`/clients/${clientId}/quickbooks/link/reset`);
  },

  /**
   * Disconnect QuickBooks account
   */
  disconnect: async (clientId: string): Promise<void> => {
    await apiClient.post(`/clients/${clientId}/quickbooks/link/disconnect`);
  },

  /**
   * Validate QuickBooks connection status
   */
  validateConnection: async (clientId: string): Promise<QuickBooksLinkStatus> => {
    const response = await apiClient.get<QuickBooksLinkStatus>(
      `/clients/${clientId}/quickbooks/connection/validate`
    );
    return response.data;
  },

  /**
   * Get QuickBooks company info
   */
  getCompanyInfo: async (clientId: string): Promise<QuickBooksCompanyInfo> => {
    const response = await apiClient.get<QuickBooksCompanyInfo>(
      `/clients/${clientId}/quickbooks/company-info`
    );
    return response.data;
  },

  /**
   * Fetch accounts from QuickBooks (chart of accounts)
   */
  fetchAccounts: async (clientId: string): Promise<QuickBooksFetchAccountsResponse> => {
    const response = await apiClient.post<QuickBooksFetchAccountsResponse>(
      `/clients/${clientId}/quickbooks/accounts/fetch`
    );
    return response.data;
  },

  /**
   * Refresh QuickBooks access token
   */
  refreshToken: async (clientId: string): Promise<QuickBooksTokenRefreshResponse> => {
    const response = await apiClient.post<QuickBooksTokenRefreshResponse>(
      `/clients/${clientId}/quickbooks/token/refresh`
    );
    return response.data;
  },

  /**
   * Fetch general ledger from QuickBooks
   */
  fetchGeneralLedger: async (
    clientId: string,
    startDate: string,
    endDate: string,
    accountIds?: string[]
  ): Promise<any> => {
    const response = await apiClient.post(
      `/clients/${clientId}/quickbooks/general-ledger/fetch`,
      {
        start_date: startDate,
        end_date: endDate,
        account_ids: accountIds,
      }
    );
    return response.data;
  },

  /**
   * Sync transactions from QuickBooks.
   * Fetches all Trust account deposits and purchases from QuickBooks
   * and imports them as ledger entries.
   */
  syncTransactions: async (clientId: string): Promise<QuickBooksSyncResponse> => {
    const response = await apiClient.post<QuickBooksSyncResponse>(
      `/clients/${clientId}/quickbooks/transactions/sync`
    );
    return response.data;
  },

  // Legacy aliases for backwards compatibility with existing code
  getAuthUrl: async (clientId: string): Promise<string> => {
    const response = await quickbooksApi.startLink(clientId);
    return response.authorization_url;
  },

  getLinkStatus: async (clientId: string): Promise<{ linked: boolean; company_name: string | null; realm_id: string | null }> => {
    const status = await quickbooksApi.validateConnection(clientId);
    return {
      linked: status.is_valid,
      company_name: status.company_name || null,
      realm_id: status.realm_id || null,
    };
  },

  unlinkAccount: async (clientId: string): Promise<void> => {
    return quickbooksApi.disconnect(clientId);
  },

  getAccounts: async (clientId: string): Promise<QuickBooksAccount[]> => {
    const response = await quickbooksApi.fetchAccounts(clientId);
    return response.accounts;
  },

  testConnection: async (clientId: string): Promise<{ connected: boolean }> => {
    const status = await quickbooksApi.validateConnection(clientId);
    return { connected: status.is_valid };
  },

  /**
   * Sync a ledger entry's allocations to QuickBooks.
   * Creates/finds customers in QB and updates the deposit/purchase with customer line items.
   */
  syncLedgerEntry: async (clientId: string, ledgerEntryId: string): Promise<void> => {
    await apiClient.post(
      `/clients/${clientId}/quickbooks/ledger-entry/${ledgerEntryId}/sync`
    );
  },

  /**
   * Validate QuickBooks credentials (realm_id and refresh_token) without storing them.
   * This allows users to test credentials before saving.
   */
  validateCredentials: async (
    clientId: string,
    realmId: string,
    refreshToken: string
  ): Promise<QuickBooksLinkStatus> => {
    const response = await apiClient.post<QuickBooksLinkStatus>(
      `/clients/${clientId}/quickbooks/credentials/validate`,
      {
        realm_id: realmId,
        refresh_token: refreshToken,
      }
    );
    return response.data;
  },

  /**
   * Get the configured Trust Account Liabilities account
   */
  getTrustAccountLiabilities: async (clientId: string): Promise<TrustAccountLiabilitiesResponse> => {
    const response = await apiClient.get<TrustAccountLiabilitiesResponse>(
      `/clients/${clientId}/quickbooks/trust-account-liabilities`
    );
    return response.data;
  },

  /**
   * Set the Trust Account Liabilities account for QuickBooks sync
   */
  setTrustAccountLiabilities: async (clientId: string, qboAccountId: string): Promise<TrustAccountLiabilitiesResponse> => {
    const response = await apiClient.post<TrustAccountLiabilitiesResponse>(
      `/clients/${clientId}/quickbooks/trust-account-liabilities`,
      { qbo_account_id: qboAccountId }
    );
    return response.data;
  },
};
